import { useEffect, useRef, useState } from 'react'

/**
 * CameraCaptureModal — live webcam capture for the Clinical Identification Photo.
 *
 * On desktop, `<input type="file" capture="user">` is only a hint that mobile
 * browsers honor; desktop Chrome / Firefox / Edge ignore it and jump straight
 * to the file picker. Real webcam capture on desktop requires `getUserMedia`
 * plus a `<video>` + `<canvas>` frame extraction, which is what this modal
 * provides.
 *
 * Flow:
 *   1. Modal mounts and prompts the browser for camera permission.
 *   2. Live preview shows in a `<video>` element (front camera preferred).
 *   3. "Capture" draws the current frame onto a hidden `<canvas>`, extracts a
 *      PNG blob, hands it to `onCapture(file)`, and closes the stream.
 *   4. "Cancel" or Esc closes without a capture.
 *
 * MediaStream cleanup runs on unmount so leaving the modal (by capturing,
 * cancelling, or an outside-click) always releases the camera indicator on
 * the OS. Without this the tab keeps holding the camera even after the
 * modal is gone — the classic "why is my webcam light still on?" bug.
 */
export default function CameraCaptureModal({ onCapture, onCancel }) {
  const videoRef = useRef(null)
  const canvasRef = useRef(null)
  const streamRef = useRef(null)
  const [error, setError] = useState('')
  const [isCapturing, setIsCapturing] = useState(false)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    let cancelled = false

    async function start() {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          setError(
            'Your browser does not support live camera capture. ' +
            'Use the Upload photo option instead.',
          )
          return
        }
        // facingMode 'user' asks for front camera on devices that have both.
        // On desktop, browsers pick the default webcam and ignore the hint.
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 720 } },
          audio: false,
        })
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop())
          return
        }
        streamRef.current = stream
        if (videoRef.current) {
          videoRef.current.srcObject = stream
          // Autoplay is also declared as an attribute for defence in depth —
          // muted + playsInline + autoplay together satisfy browser autoplay
          // policies without a user gesture.
          await videoRef.current.play().catch(() => {})
          setIsReady(true)
        }
      } catch (err) {
        // Common errors: NotAllowedError (user denied), NotFoundError (no
        // camera), NotReadableError (camera in use by another app). Surface
        // a plain-language message and let the user fall back to upload.
        const msg =
          err?.name === 'NotAllowedError'
            ? 'Camera access was denied. Allow camera permission or use Upload photo instead.'
            : err?.name === 'NotFoundError'
            ? 'No camera detected on this device. Use Upload photo instead.'
            : err?.name === 'NotReadableError'
            ? 'Camera is in use by another app. Close the other app and try again, or use Upload photo.'
            : 'Could not start the camera. Use Upload photo instead.'
        setError(msg)
      }
    }

    start()

    return () => {
      cancelled = true
      // Stop all tracks so the OS camera indicator turns off cleanly.
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop())
        streamRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onCancel?.()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onCancel])

  async function capture() {
    if (!videoRef.current || !canvasRef.current) return
    setIsCapturing(true)
    try {
      const video = videoRef.current
      const canvas = canvasRef.current

      // Draw the current video frame at native resolution so the resulting
      // PNG is as sharp as the sensor allows. Falls back to 480x480 if the
      // video hasn't reported dimensions yet.
      const w = video.videoWidth || 480
      const h = video.videoHeight || 480
      canvas.width = w
      canvas.height = h
      canvas.getContext('2d').drawImage(video, 0, 0, w, h)

      const blob = await new Promise((resolve) =>
        canvas.toBlob((b) => resolve(b), 'image/png', 0.92),
      )
      if (!blob) {
        setError('Could not capture a frame. Try again.')
        setIsCapturing(false)
        return
      }

      const filename = `identity-${Date.now()}.png`
      const file = new File([blob], filename, { type: 'image/png' })
      onCapture?.(file)
    } catch (err) {
      setError('Capture failed. Try again or use Upload photo.')
      setIsCapturing(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="camera-capture-title"
    >
      {/*
        Modal container is height-constrained to the viewport and flex-columns
        so header/body/footer split the available space without pushing
        Capture/Cancel below the fold. Body is the only scrollable region;
        the sticky action bar keeps the primary CTA (Capture) always visible.
      */}
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[95vh] flex flex-col">
        <div className="p-4 border-b border-slate-100 flex items-start justify-between gap-4 shrink-0">
          <div>
            <h2 id="camera-capture-title" className="text-lg font-semibold text-slate-900">
              Take Clinical Identification Photo
            </h2>
            <p className="text-xs text-slate-500 mt-0.5">
              Ask the patient to face the camera. Capture when their face is
              clear and well-lit.
            </p>
          </div>
        </div>

        <div className="p-3 sm:p-4 flex-1 overflow-y-auto min-h-0">
          {/*
            Video wrapper caps its height at ~55vh (with padding + headers
            leaves room for the footer bar on typical laptop screens). Uses
            an inline max-height style so it's not overridden by any parent
            aspect utility. `object-contain` on the video keeps the full
            camera frame visible even if it's wider than tall.
          */}
          <div
            className="relative w-full bg-slate-900 rounded overflow-hidden mx-auto"
            style={{ maxHeight: '55vh', aspectRatio: '4 / 3' }}
          >
            <video
              ref={videoRef}
              className="absolute inset-0 w-full h-full object-contain bg-slate-900"
              playsInline
              muted
              autoPlay
            />
            {!isReady && !error && (
              <div className="absolute inset-0 flex items-center justify-center text-slate-300 text-sm">
                Starting camera…
              </div>
            )}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center p-6 text-center text-slate-100 text-sm bg-slate-900/80">
                {error}
              </div>
            )}
          </div>
          <canvas ref={canvasRef} className="hidden" />
        </div>

        <div className="p-3 sm:p-4 flex justify-end gap-2 border-t border-slate-100 bg-white shrink-0">
          <button
            type="button"
            onClick={onCancel}
            disabled={isCapturing}
            className="text-sm px-3 py-1.5 rounded border border-slate-300 hover:bg-slate-50 text-slate-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={capture}
            disabled={!isReady || isCapturing || !!error}
            className="text-sm px-4 py-1.5 rounded bg-wantok-700 hover:bg-wantok-800 disabled:opacity-50 text-white font-medium"
          >
            {isCapturing ? 'Capturing…' : 'Capture'}
          </button>
        </div>
      </div>
    </div>
  )
}
